import {
  canAnywhere,
  type OfferView,
  PROCUREMENT_STAGES,
  type ProcurementStage,
  type PurchaseRequestDetail,
  REQUEST_SERVICE_STATUSES,
  SCORE_CRITERIA,
  type ScoreCriterion,
  STAGE_TRANSITIONS,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { Download, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { DiscussionPanel } from '../../components/DiscussionPanel';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, percent, plural, tenge, tengeShort, todayLocal } from '../../lib/format';
import { counterpartiesQuery } from '../../lib/projects';
import { useCan, useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Page, Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { NewChangeDialog } from '../changes/ChangesPage';
import { useProjectsWith } from '../psd/shared';
import { NewRequestDialog } from './ProcurementPage';
import { RequestPaymentsPanel } from './payments';
import { SplitBox, ThreeOffersBox } from './split';

const fmt2 = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
const fmt1 = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const parseNum = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.'));

function OfferDialog({
  request,
  offer,
  onClose,
}: {
  request: PurchaseRequestDetail;
  offer: OfferView | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const suppliers = useQuery(counterpartiesQuery('supplier'));
  const [f, setF] = useState({
    supplierId: offer?.supplierId ?? '',
    supplierName: '',
    deliveryIncluded: offer?.deliveryIncluded ?? true,
    deliveryCost: offer?.deliveryCost?.toString() ?? '',
    deliveryDays: offer?.deliveryDays?.toString() ?? '',
    advancePct: offer?.advancePct?.toString() ?? '',
    deferralDays: offer?.deferralDays?.toString() ?? '',
    paymentTerms: offer?.paymentTerms ?? '',
    documentsOk: offer?.documentsOk ?? true,
    receivedOn: offer?.receivedOn ?? todayLocal(),
    validUntil: offer?.validUntil ?? '',
    note: offer?.note ?? '',
  });
  const [prices, setPrices] = useState<Record<string, string>>(() =>
    Object.fromEntries(request.items.map((i) => [i.id, offer?.prices[i.id]?.toString() ?? ''])),
  );
  const [extras, setExtras] = useState<
    Array<{ k: string; name: string; unit: string; quantity: string; price: string; note: string }>
  >(() =>
    (offer?.extras ?? []).map((x, i) => ({
      k: `e${i}`,
      name: x.name,
      unit: x.unit,
      quantity: String(x.quantity),
      price: x.price !== null ? String(x.price) : '',
      note: x.note ?? '',
    })),
  );
  const setExtra = (k: string, patch: Partial<(typeof extras)[number]>) =>
    setExtras((xs) => xs.map((x) => (x.k === k ? { ...x, ...patch } : x)));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        supplierId: f.supplierId || null,
        supplierName: f.supplierId ? undefined : f.supplierName,
        deliveryIncluded: f.deliveryIncluded,
        deliveryCost: f.deliveryCost ? parseNum(f.deliveryCost) : null,
        deliveryDays: f.deliveryDays ? Math.round(parseNum(f.deliveryDays)) : null,
        advancePct: f.advancePct ? parseNum(f.advancePct) : null,
        deferralDays: f.deferralDays ? Math.round(parseNum(f.deferralDays)) : null,
        paymentTerms: f.paymentTerms || null,
        documentsOk: f.documentsOk,
        receivedOn: f.receivedOn || null,
        validUntil: f.validUntil || null,
        note: f.note || null,
        prices: Object.fromEntries(
          Object.entries(prices).map(([k, v]) => [k, v.trim() ? parseNum(v) : null]),
        ),
        extras: extras
          .filter((x) => x.name.trim().length >= 2 && x.unit.trim() && parseNum(x.quantity) > 0)
          .map((x) => ({
            name: x.name.trim(),
            unit: x.unit.trim(),
            quantity: parseNum(x.quantity),
            price: x.price.trim() ? parseNum(x.price) : null,
            note: x.note.trim() || null,
          })),
      };
      return offer
        ? api<PurchaseRequestDetail>(`/procurement/offers/${offer.id}`, { method: 'PATCH', body })
        : api<PurchaseRequestDetail>(`/procurement/requests/${request.id}/offers`, { body });
    },
    onSuccess: (d) => {
      qc.setQueryData(['procurement', 'request', request.id], d);
      qc.invalidateQueries({ queryKey: ['procurement', 'requests'] });
      qc.invalidateQueries({ queryKey: ['attention'] });
      toast(offer ? 'КП сохранено' : 'КП добавлено');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      width="lg"
      title={offer ? `КП «${offer.supplierName}»` : 'Новое КП'}
      description="Цены за единицу с НДС; пустая цена — позиции нет в КП."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!f.supplierId && f.supplierName.trim().length < 2}
            onClick={() => save.mutate()}
          >
            Сохранить КП
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Поставщик">
          <Select value={f.supplierId} onChange={(e) => setF({ ...f, supplierId: e.target.value })}>
            <option value="">— новый —</option>
            {(suppliers.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        {!f.supplierId ? (
          <Field label="Новый поставщик">
            <Input value={f.supplierName} onChange={(e) => setF({ ...f, supplierName: e.target.value })} />
          </Field>
        ) : (
          <div />
        )}
        <Field label="Условия оплаты">
          <Input
            value={f.paymentTerms}
            placeholder="отсрочка 30 дней"
            onChange={(e) => setF({ ...f, paymentTerms: e.target.value })}
          />
        </Field>
        <Field label="Доставка отдельно, ₸" hint={f.deliveryIncluded ? 'включена в цены' : undefined}>
          <Input
            inputMode="decimal"
            value={f.deliveryCost}
            disabled={f.deliveryIncluded}
            onChange={(e) => setF({ ...f, deliveryCost: e.target.value })}
          />
        </Field>
        <Field label="Срок поставки, дней">
          <Input
            inputMode="numeric"
            value={f.deliveryDays}
            onChange={(e) => setF({ ...f, deliveryDays: e.target.value })}
          />
        </Field>
        <Field label="Аванс, %">
          <Input
            inputMode="decimal"
            value={f.advancePct}
            onChange={(e) => setF({ ...f, advancePct: e.target.value })}
          />
        </Field>
        <Field label="Отсрочка, дней">
          <Input
            inputMode="numeric"
            value={f.deferralDays}
            onChange={(e) => setF({ ...f, deferralDays: e.target.value })}
          />
        </Field>
        <Field label="Получено">
          <Input
            type="date"
            value={f.receivedOn}
            onChange={(e) => setF({ ...f, receivedOn: e.target.value })}
          />
        </Field>
        <Field label="Действует до">
          <Input
            type="date"
            value={f.validUntil}
            onChange={(e) => setF({ ...f, validUntil: e.target.value })}
          />
        </Field>
      </div>
      <div className="mt-3 flex flex-wrap gap-6">
        <Checkbox
          label="Доставка включена в цены"
          checked={f.deliveryIncluded}
          onChange={(e) => setF({ ...f, deliveryIncluded: e.target.checked })}
        />
        <Checkbox
          label="Документы поставщика проверены"
          checked={f.documentsOk}
          onChange={(e) => setF({ ...f, documentsOk: e.target.checked })}
        />
      </div>
      <table className="mt-4 w-full text-[13px]">
        <thead>
          <tr className="text-left text-[12px] text-ink-2">
            <th className="py-1.5 font-medium">Позиция</th>
            <th className="py-1.5 text-right font-medium">Кол-во</th>
            <th className="py-1.5 text-right font-medium">Смета, ₸/ед.</th>
            <th className="py-1.5 pl-3 font-medium">Цена КП, ₸/ед.</th>
          </tr>
        </thead>
        <tbody>
          {request.items.map((i) => (
            <tr key={i.id} className="border-t border-line">
              <td className="py-1.5">{i.name}</td>
              <td className="num py-1.5 text-right">
                {fmt2.format(i.quantity)} {i.unit}
              </td>
              <td className="num py-1.5 text-right text-ink-2">
                {i.estimatePrice !== null ? fmt2.format(i.estimatePrice) : '—'}
              </td>
              <td className="py-1.5 pl-3">
                <Input
                  className="h-8"
                  inputMode="decimal"
                  value={prices[i.id] ?? ''}
                  onChange={(e) => setPrices({ ...prices, [i.id]: e.target.value })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-[13px] font-medium">Сверх запроса (ЗК-31)</span>
          <Button
            size="sm"
            variant="ghost"
            icon={<Plus className="size-4" />}
            onClick={() =>
              setExtras([
                ...extras,
                { k: `n${Date.now()}`, name: '', unit: '', quantity: '', price: '', note: '' },
              ])
            }
          >
            Позиция
          </Button>
        </div>
        {extras.length ? (
          <div className="flex flex-col gap-2">
            {extras.map((x) => (
              <div key={x.k} className="grid grid-cols-[2fr_70px_90px_120px_1.5fr_auto] items-center gap-2">
                <Input
                  className="h-8"
                  placeholder="Наименование"
                  value={x.name}
                  onChange={(e) => setExtra(x.k, { name: e.target.value })}
                />
                <Input
                  className="h-8"
                  placeholder="ед."
                  value={x.unit}
                  onChange={(e) => setExtra(x.k, { unit: e.target.value })}
                />
                <Input
                  className="h-8"
                  inputMode="decimal"
                  placeholder="кол-во"
                  value={x.quantity}
                  onChange={(e) => setExtra(x.k, { quantity: e.target.value })}
                />
                <Input
                  className="h-8"
                  inputMode="decimal"
                  placeholder="цена, ₸"
                  value={x.price}
                  onChange={(e) => setExtra(x.k, { price: e.target.value })}
                />
                <Input
                  className="h-8"
                  placeholder="примечание"
                  value={x.note}
                  onChange={(e) => setExtra(x.k, { note: e.target.value })}
                />
                <IconButton
                  label="Удалить позицию"
                  onClick={() => setExtras(extras.filter((y) => y.k !== x.k))}
                >
                  <Trash2 className="size-4" />
                </IconButton>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-ink-2">
            Например, водопонижение, которое поставщик добавил в КП сам.
          </p>
        )}
      </div>
      <Field label="Примечание" className="mt-4">
        <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </Field>
      <ErrorBox error={save.error} className="mt-4" />
    </Dialog>
  );
}

/** ЗК-21: веса критериев сравнения; изменение шаблона — с обоснованием */
function WeightsDialog({ d, onClose }: { d: PurchaseRequestDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const [w, setW] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (Object.keys(SCORE_CRITERIA) as ScoreCriterion[]).map((k) => [k, String(d.weights[k] ?? 0)]),
    ),
  );
  const [reason, setReason] = useState(d.weightsReason ?? '');
  const m = useMutation({
    mutationFn: () =>
      api<PurchaseRequestDetail>(`/procurement/requests/${d.id}/weights`, {
        method: 'PUT',
        body: {
          weights: Object.fromEntries(Object.entries(w).map(([k, v]) => [k, parseNum(v) || 0])),
          reason: reason.trim(),
        },
      }),
    onSuccess: (x) => {
      qc.setQueryData(['procurement', 'request', d.id], x);
      toast('Веса сохранены, баллы пересчитаны');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Веса критериев сравнения"
      description="Шаблон категории: цена 50, срок 15, аванс и отсрочка 15, документы 10, полнота 10. Изменение — с обоснованием."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={reason.trim().length < 5}
            onClick={() => m.mutate()}
          >
            Сохранить веса
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {(Object.keys(SCORE_CRITERIA) as ScoreCriterion[]).map((k) => (
          <Field key={k} label={SCORE_CRITERIA[k]}>
            <Input
              inputMode="numeric"
              value={w[k] ?? ''}
              onChange={(e) => setW({ ...w, [k]: e.target.value })}
            />
          </Field>
        ))}
        <Field label="Обоснование" className="sm:col-span-2">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
      <ErrorBox error={m.error} className="mt-3" />
    </Dialog>
  );
}

/** ЗК-31: что поставщик не включил и что добавил сверх запроса — отдельно от суммы сравнения */
function ExceptionsAndExtras({ d }: { d: PurchaseRequestDetail }) {
  const rows = d.offerList.filter((o) => o.missing > 0 || o.extras.length > 0);
  if (!rows.length) return null;
  return (
    <div className="border-t border-line px-5 py-4">
      <h3 className="mb-2 text-[14px] font-semibold">Исключения и дополнительные позиции</h3>
      <p className="mb-3 text-[13px] text-ink-2">
        В сумму сравнения не входят: исключённые позиции надо докупить отдельно, дополнительные — оценить,
        нужны ли они.
      </p>
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-[12px] text-ink-2">
            <th className="py-1.5 pr-3 font-medium">Поставщик</th>
            <th className="py-1.5 pr-3 font-medium">Не включено в КП</th>
            <th className="py-1.5 pr-3 font-medium">Сверх запроса</th>
            <th className="py-1.5 text-right font-medium">Сумма сверх запроса</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id} className="border-t border-line align-top">
              <td className="py-2 pr-3 font-medium">{o.supplierName}</td>
              <td className="py-2 pr-3">
                {o.missing
                  ? d.items
                      .filter((i) => o.prices[i.id] === null || o.prices[i.id] === undefined)
                      .map((i) => i.name)
                      .join('; ') || `${o.missing} поз.`
                  : '—'}
              </td>
              <td className="py-2 pr-3">
                {o.extras.length
                  ? o.extras.map((x) => (
                      <div key={`${x.name}-${x.unit}`}>
                        {x.name}, {fmt2.format(x.quantity)} {x.unit}
                        {x.price !== null ? ` × ${fmt2.format(x.price)} ₸` : ''}
                        {x.note ? <span className="text-ink-2"> — {x.note}</span> : null}
                      </div>
                    ))
                  : '—'}
              </td>
              <td className="num py-2 text-right">{o.extras.length ? tenge(o.extrasTotal) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Comparison({
  d,
  canManage,
  canAnalyse,
}: {
  d: PurchaseRequestDetail;
  canManage: boolean;
  canAnalyse: boolean;
}) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<OfferView | null | 'new'>(null);
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [reason, setReason] = useState(d.selectionReason ?? '');
  const [no3, setNo3] = useState(d.noThreeOffersReason ?? '');
  // Без доступа к суммам пояснения с суммами видны с маской и только для чтения (ДС-04)
  const me = useMe();
  const blind = !seesMoney(me.access, 'acts_invoices');
  const lockReason = blind && (d.selectionReason ?? '').includes('сумма скрыта');
  const lockNo3 = blind && (d.noThreeOffersReason ?? '').includes('сумма скрыта');
  const select = useMutation({
    mutationFn: (body: { offerId: string | null; reason?: string; noThreeOffersReason?: string }) =>
      api<PurchaseRequestDetail>(`/procurement/requests/${d.id}/select`, { body }),
    onSuccess: (x) => {
      qc.setQueryData(['procurement', 'request', d.id], x);
      qc.invalidateQueries({ queryKey: ['procurement', 'requests'] });
      qc.invalidateQueries({ queryKey: ['attention'] });
      toast('Сохранено');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/procurement/offers/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['procurement'] });
      toast('КП удалено');
    },
  });
  const editable = canManage && ['rfq', 'analysis'].includes(d.stage);
  const selecting = canAnalyse && d.stage === 'analysis';
  // Подобрать со склада можно, пока поставщик не выбран и есть доступ к складу
  const sourcing =
    useCan('warehouse.view') && ['new', 'review', 'rfq', 'analysis', 'returned'].includes(d.stage);
  const est = d.estimateTotal;
  const sel = d.offerList.find((o) => o.isSelected);

  return (
    <Panel
      title="Сравнение КП"
      meta={`${plural(d.offerList.length, 'предложение', 'предложения', 'предложений')}, цены с НДС`}
      bodyClassName="p-0"
      actions={
        <>
          {d.offerList.length && d.estimateTotal !== null ? (
            <a
              href={`/api/v1/procurement/requests/${d.id}/analysis.xlsx`}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line-strong bg-surface px-3 text-[13px] text-ink hover:underline"
            >
              <Download className="size-4" /> Файл КА
            </a>
          ) : null}
          {d.offerList.length && d.estimateTotal !== null ? (
            <a
              href={`/api/v1/procurement/requests/${d.id}/analysis.pdf`}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md border border-line-strong bg-surface px-3 text-[13px] text-ink hover:underline md:h-8 md:min-h-0"
            >
              <Download className="size-4" /> КА в PDF
            </a>
          ) : null}
          {(editable || selecting) && d.offerList.length ? (
            <Button size="sm" variant="ghost" onClick={() => setWeightsOpen(true)}>
              Веса
            </Button>
          ) : null}
          {editable ? (
            <Button
              size="sm"
              variant="secondary"
              icon={<Plus className="size-4" />}
              onClick={() => setEdit('new')}
            >
              КП
            </Button>
          ) : null}
        </>
      }
    >
      <ScrollX>
        <table className="text-[13px]">
          <thead>
            <tr className="align-bottom text-left text-[12px] text-ink-2">
              <th className="sticky left-0 z-10 min-w-[220px] bg-surface-2 py-2 pr-3 pl-5 font-medium">
                Позиция
              </th>
              <th className="bg-surface-2 px-3 py-2 text-right font-medium">Кол-во</th>
              <th className="bg-surface-2 px-3 py-2 text-right font-medium">Смета, ₸/ед.</th>
              {d.offerList.map((o) => (
                <th
                  key={o.id}
                  className={clsx(
                    'min-w-[150px] border-l border-line px-3 py-2 font-medium',
                    o.isSelected ? 'bg-accent-soft' : 'bg-surface-2',
                  )}
                >
                  <div className="flex items-start justify-between gap-1">
                    <span className="text-[13px] font-semibold text-ink">{o.supplierName}</span>
                    {editable ? (
                      <span className="flex shrink-0">
                        <IconButton className="size-7" label="Изменить КП" onClick={() => setEdit(o)}>
                          <Pencil className="size-3.5" />
                        </IconButton>
                        <IconButton className="size-7" label="Удалить КП" onClick={() => del.mutate(o.id)}>
                          <Trash2 className="size-3.5" />
                        </IconButton>
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {!o.documentsOk ? <Badge tone="danger">документы не прошли проверку</Badge> : null}
                    {o.missing ? <Badge tone="warning">нет цен: {o.missing}</Badge> : null}
                    {o.isCheapest ? <Badge tone="success">дешевле всех</Badge> : null}
                    {o.isSelected ? <Badge tone="accent">выбран</Badge> : null}
                  </div>
                  <div className="mt-1 font-normal">
                    {o.paymentTerms ?? 'оплата: условия не указаны'}
                    {o.note ? `; ${o.note}` : ''}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.items.map((i) => (
              <tr key={i.id} className="border-t border-line">
                <td className="sticky left-0 z-[1] bg-surface py-2 pr-3 pl-5">
                  {i.name}
                  {/* Подбор со склада перед закупкой (СК-12…СК-15, поток 5): похожее на складах компании, «Взять» ставит резерв под строку */}
                  {sourcing ? (
                    <Link
                      to="/warehouse"
                      search={{ line: i.id }}
                      className="ml-2 inline-flex min-h-11 items-center text-[12px] text-ink-2 underline-offset-4 hover:text-ink hover:underline"
                    >
                      подобрать со склада
                    </Link>
                  ) : null}
                </td>
                <td className="num px-3 py-2 text-right whitespace-nowrap">
                  {fmt2.format(i.quantity)} {i.unit}
                  {i.fromStock > 0 || i.fromStockRequested > 0 ? (
                    <div
                      className="text-[12px] font-normal text-ink-2"
                      title="Взято со склада резервом под эту строку"
                    >
                      со склада {fmt2.format(i.fromStock)}
                      {i.fromStockRequested > 0
                        ? ` (+${fmt2.format(i.fromStockRequested)} ждёт согласия)`
                        : ''}
                      {i.fromStock > 0 && i.fromStock < i.quantity
                        ? ` · к закупке ${fmt2.format(i.quantity - i.fromStock)}`
                        : ''}
                    </div>
                  ) : null}
                </td>
                <td className="num px-3 py-2 text-right whitespace-nowrap text-ink-2">
                  {i.estimatePrice !== null ? fmt2.format(i.estimatePrice) : '—'}
                </td>
                {d.offerList.map((o) => {
                  const p = o.prices[i.id];
                  const best = p !== null && p !== undefined && p === i.bestPrice && o.documentsOk;
                  const over =
                    p !== null && p !== undefined && i.estimatePrice !== null && p > i.estimatePrice;
                  return (
                    <td
                      key={o.id}
                      className={clsx(
                        'num border-l border-line px-3 py-2 text-right whitespace-nowrap',
                        o.isSelected && 'bg-accent-soft/40',
                        best && 'font-semibold',
                        over && 'text-danger',
                        (p === null || p === undefined) && 'text-ink-3',
                      )}
                    >
                      {p === null || p === undefined ? 'нет цены' : fmt2.format(p)}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="border-t-2 border-line-strong font-semibold">
              <td className="sticky left-0 z-[1] bg-surface py-2 pr-3 pl-5">Итого с доставкой</td>
              <td />
              <td className="num px-3 py-2 text-right whitespace-nowrap">
                {est !== null ? tenge(est) : '—'}
              </td>
              {d.offerList.map((o) => (
                <td
                  key={o.id}
                  className={clsx(
                    'num border-l border-line px-3 py-2 text-right whitespace-nowrap',
                    o.isSelected && 'bg-accent-soft/40',
                  )}
                >
                  {tenge(o.total)}
                  <div className="text-[12px] font-normal text-ink-2">
                    {o.deliveryIncluded
                      ? 'доставка в цене'
                      : o.deliveryCost
                        ? `доставка ${fmt2.format(o.deliveryCost)}`
                        : 'без доставки'}
                  </div>
                </td>
              ))}
            </tr>
            <tr className="border-t border-line">
              <td className="sticky left-0 z-[1] bg-surface py-2 pr-3 pl-5 text-ink-2">
                К смете по позициям с ценой
              </td>
              <td />
              <td />
              {d.offerList.map((o) => (
                <td
                  key={o.id}
                  className={clsx(
                    'num border-l border-line px-3 py-2 text-right',
                    o.vsEstimatePct !== null && o.vsEstimatePct > 0 ? 'text-danger' : 'text-success',
                  )}
                >
                  {o.vsEstimatePct === null
                    ? '—'
                    : `${o.vsEstimatePct > 0 ? '+' : ''}${percent(o.vsEstimatePct)}`}
                </td>
              ))}
            </tr>
            {d.offerList.some((o) => o.scores)
              ? (Object.keys(SCORE_CRITERIA) as ScoreCriterion[]).map((k) => (
                  <tr key={k} className="border-t border-line text-[13px]">
                    <td className="sticky left-0 z-[1] bg-surface py-1.5 pr-3 pl-5 text-ink-2">
                      Балл: {SCORE_CRITERIA[k].toLowerCase()}{' '}
                      <span className="num">({d.weights[k] ?? 0})</span>
                    </td>
                    <td />
                    <td />
                    {d.offerList.map((o) => (
                      <td key={o.id} className="num border-l border-line px-3 py-1.5 text-right text-ink-2">
                        {o.scores ? fmt1.format(o.scores[k] ?? 0) : '—'}
                      </td>
                    ))}
                  </tr>
                ))
              : null}
            {d.offerList.some((o) => o.score !== null) ? (
              <tr className="border-t border-line font-semibold">
                <td className="sticky left-0 z-[1] bg-surface py-2 pr-3 pl-5">
                  Итоговый балл
                  <span className="block text-[12px] font-normal text-ink-2">
                    {d.weightsChanged ? `веса изменены: ${d.weightsReason ?? ''}` : 'веса по шаблону (ЗК-21)'}
                  </span>
                </td>
                <td />
                <td />
                {d.offerList.map((o) => (
                  <td
                    key={o.id}
                    className={clsx(
                      'num border-l border-line px-3 py-2 text-right',
                      o.isBestScore && 'text-success',
                    )}
                  >
                    {o.score !== null ? fmt1.format(o.score) : '—'}
                    {o.isBestScore ? <div className="text-[12px] font-normal">лучший балл</div> : null}
                  </td>
                ))}
              </tr>
            ) : null}
            {selecting ? (
              <tr className="border-t border-line">
                <td className="sticky left-0 z-[1] bg-surface py-2 pr-3 pl-5 text-ink-2">Выбор поставщика</td>
                <td />
                <td />
                {d.offerList.map((o) => (
                  <td key={o.id} className="border-l border-line px-3 py-2">
                    {o.documentsOk ? (
                      <Button
                        size="sm"
                        variant={o.isSelected ? 'primary' : 'secondary'}
                        onClick={() => select.mutate({ offerId: o.isSelected ? null : o.id })}
                      >
                        {o.isSelected ? 'Выбран' : 'Выбрать'}
                      </Button>
                    ) : (
                      <span className="text-[12px] text-ink-3">участие закрыто</span>
                    )}
                  </td>
                ))}
              </tr>
            ) : null}
          </tbody>
        </table>
      </ScrollX>
      <ExceptionsAndExtras d={d} />
      {selecting || d.selectionReason || d.noThreeOffersReason ? (
        <div className="grid gap-4 border-t border-line p-5 md:grid-cols-2">
          <Field
            label="Почему выбраны этот поставщик и эта цена"
            hint={
              sel && (!sel.isCheapest || (est !== null && sel.total > est))
                ? 'Обязательно: выбран не самый дешёвый КП или дороже сметы (ЗК-30)'
                : 'Приказ № 31П, п. 2'
            }
          >
            <Textarea
              rows={3}
              value={reason}
              disabled={!selecting || lockReason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <Field
            label="Обоснование закупки без трёх КП"
            hint="Нужно, если КП меньше трёх, а сумма выше порога трёх КП компании (ЗК-05)"
          >
            <Textarea
              rows={3}
              value={no3}
              disabled={(!selecting && !editable) || lockNo3}
              onChange={(e) => setNo3(e.target.value)}
            />
          </Field>
          {selecting || editable ? (
            <div className="md:col-span-2">
              <Button
                variant="secondary"
                loading={select.isPending}
                onClick={() => select.mutate({ offerId: sel?.id ?? null, reason, noThreeOffersReason: no3 })}
              >
                Сохранить пояснения
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {edit ? (
        <OfferDialog request={d} offer={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />
      ) : null}
      {weightsOpen ? <WeightsDialog d={d} onClose={() => setWeightsOpen(false)} /> : null}
    </Panel>
  );
}

export function RequestPage() {
  const { requestId } = useParams({ strict: false }) as { requestId: string };
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['procurement', 'request', requestId],
    queryFn: () => api<PurchaseRequestDetail>(`/procurement/requests/${requestId}`),
  });
  useCrumbs(
    q.data
      ? [
          {
            label: q.data.projectCode,
            to: `/projects/${q.data.projectId}/procurement`,
            title: `${q.data.projectCode} ${q.data.projectName}`,
          },
          { label: q.data.number, title: `${q.data.number} ${q.data.title}` },
        ]
      : null,
    q.error,
  );
  const [comment, setComment] = useState('');
  const [pending, setPending] = useState<ProcurementStage | null>(null);
  const [editing, setEditing] = useState(false);
  const [asking, setAsking] = useState(false);
  const askers = useProjectsWith('changes.request');
  const move = useMutation({
    mutationFn: (p: { to: ProcurementStage; comment?: string }) =>
      api<PurchaseRequestDetail>(`/procurement/requests/${requestId}/transition`, { body: p }),
    onSuccess: (d) => {
      qc.setQueryData(['procurement', 'request', requestId], d);
      qc.invalidateQueries({ queryKey: ['procurement', 'requests'] });
      qc.invalidateQueries({ queryKey: ['attention'] });
      setPending(null);
      setComment('');
      toast(`Заявка: ${PROCUREMENT_STAGES[d.stage].toLowerCase()}`);
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const mkContract = useMutation({
    mutationFn: () =>
      api<{ id: string; number: string }>(`/contracts/from-request/${requestId}`, {
        body: { kind: 'supply' },
      }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['procurement'] });
      qc.invalidateQueries({ queryKey: ['contracts'] });
      toast(`Создан договор ${c.number}`);
      navigate({ to: `/contracts/${c.id}` });
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const d = q.data;
  const transitions = STAGE_TRANSITIONS.filter(
    (t) => t.from.includes(d.stage) && canAnywhere(me.access, t.permission),
  );
  const pendingRule = transitions.find((t) => t.to === pending);
  const sla = d.slaDays ?? undefined;
  // Позиции и реквизиты правятся до анализа КП (как в API)
  const canEdit =
    ['new', 'returned', 'review', 'rfq'].includes(d.stage) &&
    (canAnywhere(me.access, 'procurement.request.create') ||
      canAnywhere(me.access, 'procurement.rfq.manage'));
  // ИЗ-16: изменение объёма, цены или аналога по заявке — запросом на изменение с этой заявкой в затронутых
  const canAsk =
    (me.tenant?.staff ?? false) &&
    askers.list.some((p) => p.id === d.projectId) &&
    !['cancelled', 'closed'].includes(d.stage);

  return (
    <Page>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.01em] text-ink">
          {d.number} {d.title}
        </h1>
        <Badge tone={d.overdue ? 'danger' : 'ink'}>{PROCUREMENT_STAGES[d.stage]}</Badge>
        {d.serviceStatus ? <Badge tone="neutral">{REQUEST_SERVICE_STATUSES[d.serviceStatus]}</Badge> : null}
      </div>
      <p className="mt-1 text-[14px] text-ink-2">
        {d.projectName}
        {d.objectName ? ` · объект ${d.objectName}` : ''}
        {d.department ? ` · ${d.department}` : ''} · на этапе {d.daysOnStage} раб. дн.
        {sla ? ` из ${sla}` : ''} · {d.costArticle ?? 'статья ФП не указана'}
        {d.requiredDate ? ` · нужно к ${date(d.requiredDate)}` : ''}
        {d.buyerName ? ` · снабженец ${d.buyerName}` : ''}
      </p>
      {d.source ? <p className="mt-1 text-[13px] text-ink-2">Источник: {d.source}</p> : null}

      {d.stage === 'returned' && d.returnReason ? (
        <div className="mt-4 max-w-3xl rounded-md bg-warning-soft px-4 py-3 text-[14px] text-ink">
          Возвращена на доработку: {d.returnReason}
        </div>
      ) : null}

      {transitions.length || canEdit || canAsk ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {canEdit ? (
            <Button variant="secondary" icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>
              Изменить заявку
            </Button>
          ) : null}
          {canAsk ? (
            <Button variant="ghost" onClick={() => setAsking(true)}>
              Запрос на изменение
            </Button>
          ) : null}
          {transitions.map((t) => (
            <Button
              key={t.to}
              variant={
                t.to === 'cancelled' || t.to === 'returned'
                  ? 'ghost'
                  : t.needsComment
                    ? 'secondary'
                    : 'primary'
              }
              loading={move.isPending && pending === t.to}
              onClick={() => (t.needsComment ? setPending(t.to) : move.mutate({ to: t.to }))}
            >
              {t.label}
            </Button>
          ))}
        </div>
      ) : null}
      {d.selectedOfferId &&
      d.stage === 'contract' &&
      (canAnywhere(me.access, 'contracts.edit') || canAnywhere(me.access, 'procurement.analysis')) ? (
        <div className="mt-3">
          <Button variant="accent" loading={mkContract.isPending} onClick={() => mkContract.mutate()}>
            Создать договор из выбранного КП
          </Button>
        </div>
      ) : null}
      {asking ? (
        <NewChangeDialog
          projectId={d.projectId}
          preset={{
            type: 'material',
            links: [{ linkType: 'request', linkId: d.id, title: `${d.number} ${d.title}` }],
          }}
          onClose={() => setAsking(false)}
        />
      ) : null}
      {pendingRule ? (
        <div className="mt-3 flex max-w-2xl flex-col gap-2">
          <Textarea
            rows={2}
            placeholder="Причина"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              variant="primary"
              disabled={!comment.trim()}
              loading={move.isPending}
              onClick={() => move.mutate({ to: pendingRule.to, comment })}
            >
              {pendingRule.label}
            </Button>
            <Button variant="ghost" onClick={() => setPending(null)}>
              Отмена
            </Button>
          </div>
        </div>
      ) : null}

      {d.finCheck && (d.finCheck.planNumber !== null || d.finCheck.requestAmount !== null) ? (
        <dl className="mt-5 grid max-w-3xl grid-cols-2 gap-x-6 gap-y-1 rounded-md border border-line px-4 py-3 text-[13px] sm:grid-cols-5">
          <div className="col-span-2 sm:col-span-1">
            <dt className="text-ink-2">Статья ФП</dt>
            <dd className="text-ink">
              {d.finCheck.planNumber ? `ФП № ${d.finCheck.planNumber}` : 'ФП не утверждён'}
            </dd>
          </div>
          <div>
            <dt className="text-ink-2">План статьи</dt>
            <dd className="num text-ink">{tengeShort(d.finCheck.plan)}</dd>
          </div>
          <div>
            <dt className="text-ink-2">Законтрактовано</dt>
            <dd className="num text-ink">{tengeShort(d.finCheck.contracted)}</dd>
          </div>
          <div>
            <dt className="text-ink-2">Остаток</dt>
            <dd
              className={clsx(
                'num font-medium',
                d.finCheck.blocked || d.finCheck.over ? 'text-danger' : 'text-ink',
              )}
            >
              {tengeShort(d.finCheck.remainder)}
            </dd>
          </div>
          <div>
            <dt className="text-ink-2">
              {d.finCheck.basis === 'offer' ? 'По выбранному КП' : 'Заявка по смете'}
            </dt>
            <dd className="num text-ink">{tengeShort(d.finCheck.requestAmount)}</dd>
          </div>
        </dl>
      ) : null}

      {['rfq', 'analysis', 'approval'].includes(d.stage) || d.splits.length ? (
        <div className="mt-5 flex max-w-3xl flex-col gap-3">
          {['rfq', 'analysis', 'approval'].includes(d.stage) ? <ThreeOffersBox t={d.threeOffers} /> : null}
          <SplitBox splits={d.splits} before={!d.selectedOfferId} />
        </div>
      ) : null}

      {d.warnings.length ? (
        <div className="mt-5 rounded-md bg-warning-soft px-4 py-3 text-[14px] text-ink">
          <ul className="list-disc pl-5">
            {d.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {editing ? <NewRequestDialog open onOpenChange={setEditing} edit={d} /> : null}

      <div className="mt-6 flex flex-col gap-6">
        <RequestPaymentsPanel d={d} />
        <DiscussionPanel entityType="purchase_request" entityId={d.id} projectId={d.projectId} />
        <Comparison
          d={d}
          canManage={
            canAnywhere(me.access, 'procurement.rfq.manage') && seesMoney(me.access, 'acts_invoices')
          }
          canAnalyse={canAnywhere(me.access, 'procurement.analysis')}
        />
        <Panel title="Ход заявки">
          <ol className="flex flex-col">
            {d.events.map((e) => (
              <li key={e.id} className="relative border-l border-line pb-4 pl-5 last:pb-0">
                <span className="absolute top-1.5 -left-[4px] size-2 rounded-full bg-ink-3" aria-hidden />
                <div className="num text-[12px] text-ink-2">{dateTime(e.at)}</div>
                {e.fromStage === e.toStage && e.comment ? (
                  <div className="text-[14px] text-ink">
                    {e.comment}
                    {e.actorName ? <span className="text-ink-2">, {e.actorName}</span> : null}
                  </div>
                ) : (
                  <>
                    <div className="text-[14px] text-ink">
                      {e.fromStage
                        ? `${PROCUREMENT_STAGES[e.fromStage as ProcurementStage] ?? e.fromStage} → `
                        : ''}
                      {PROCUREMENT_STAGES[e.toStage as ProcurementStage] ?? e.toStage}
                      {e.actorName ? <span className="text-ink-2">, {e.actorName}</span> : null}
                    </div>
                    {e.comment ? <div className="text-[13px] text-ink-2">{e.comment}</div> : null}
                  </>
                )}
              </li>
            ))}
          </ol>
        </Panel>
      </div>
    </Page>
  );
}
